import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import MotionDiv from 'libs/motionDiv'
import Popup from 'libs/popup/Popup'
import Button from 'libs/button/Button'
import WhiteCard from 'libs/card/WhiteCard'
import PaginatedTable from 'libs/table/PaginatedTable'
import FullPageLoader from 'libs/loader/FullPageLoader'
import useTabTitle from 'libs/hooks/useTabTitle'
import Chip from 'libs/chip/Chip'
import { selectSelectedItem } from 'store/redux/navSlice'
import { formatUnixDate } from 'utils/dateTimeHandlers'
import { hasAccess } from 'utils/utils'
import DeleteSalesOrder from '../delete-sales-order/DeleteSalesOrder'
import { useGetSalesOrderByIDQuery } from '../sales-order-endpoints/salesOrders.endpoints'
import {
  ProductResponse,
  SalesOrder,
} from '../sales-order-endpoints/salesOrdersTypes'
import { ExtendedColumn } from 'store/services/utilsTypes'
import { CellProps } from 'react-table'

const ViewSalesOrder = () => {
  const navigate = useNavigate()
  useTabTitle('View Sales Order')
  const { id } = useParams()
  const salesOrderAccess = hasAccess('ord', { so: 'e' })
  const plantId = useSelector(selectSelectedItem)

  const [deletePopUp, setDeletePopUp] = useState<{
    isPopUpOpen: boolean
    salesOrder: SalesOrder | null
  }>({
    isPopUpOpen: false,
    salesOrder: null,
  })

  const {
    data: salesOrder,
    isLoading: salesOrderLoading,
    isFetching,
  } = useGetSalesOrderByIDQuery(
    {
      plant_id: plantId?.id,
      id: id ?? '',
    },
    {
      skip: !plantId?.id || !id,
    },
  )

  const productsColumns: ExtendedColumn<ProductResponse>[] = [
    {
      Header: 'Product',
      accessor: 'name',
      disableSortBy: true,
      Cell: ({ row }: CellProps<ProductResponse>) => {
        return (
          <div>
            <h1 className="p-xs-regular">{row.original?.name ?? '-'}</h1>
            <h2 className="p-xs-regular-dim">{row.original.code}</h2>
          </div>
        )
      },
    },
    {
      Header: 'Quantity',
      accessor: 'quantity',
      disableSortBy: true,
      align: 'right',
      Cell: ({ row }: CellProps<ProductResponse>) => {
        return <div className="mono">{row.original?.quantity ?? '-'}</div>
      },
    },
    {
      Header: 'Unit',
      disableSortBy: true,
      accessor: 'unit_of_measure',
      Cell: ({ row }: CellProps<ProductResponse>) => {
        return (
          <div className="mono">{row.original?.unit_of_measure ?? '-'}</div>
        )
      },
    },
  ]

  return (
    <MotionDiv>
      {!salesOrderLoading ? (
        <WhiteCard className="mt-3 flex flex-col gap-6">
          <span className="flex justify-between">
            <div className="flex items-center gap-2">
              <h5 className="h5">Sales Order Details</h5>
              {salesOrder?.status && (
                <Chip size="sm" title={salesOrder?.status} />
              )}
            </div>

            {salesOrderAccess && (
              <div className="flex gap-4">
                <Button
                  data-testid="delete-sales-order-btn"
                  disabled={!salesOrder}
                  onClick={() =>
                    salesOrder &&
                    setDeletePopUp({
                      isPopUpOpen: true,
                      salesOrder: salesOrder,
                    })
                  }
                  color="error"
                >
                  Delete Sales Order
                </Button>
                <Button
                  color="primary"
                  disabled={!salesOrder}
                  onClick={() =>
                    id && navigate(`/orders/sales-order/edit/${id}`)
                  }
                >
                  Edit Sales Order
                </Button>
              </div>
            )}
          </span>
          <div className="grid grid-cols-[150px,1fr] gap-4 text-xs">
            <span className="text-gray-500">Sales Order ID</span>
            <span data-testid="sales-order-id">
              {salesOrder?.salesOrderID ?? '-'}
            </span>
            <span className="text-gray-500">Description</span>
            <span data-testid="description">
              {salesOrder?.description ?? '-'}
            </span>
            <span className="text-gray-500">Customer Name</span>
            <span data-testid="customer-name">
              {salesOrder?.customer?.name ?? '-'}
            </span>
            <span className="text-gray-500">Expected Delivery Date</span>
            <span data-testid="expected-delivery-date">
              {formatUnixDate(salesOrder?.deliveryDate)}
            </span>
          </div>

          <PaginatedTable
            rows={salesOrder?.products}
            columns={productsColumns}
            emptyMessage={'No products have been added to this sales order.'}
            loading={isFetching}
            hidePagination
          />

          {deletePopUp.isPopUpOpen && deletePopUp.salesOrder && (
            <Popup
              isOpen={deletePopUp.isPopUpOpen}
              title="Delete Sales Order"
              onClose={() => {
                setDeletePopUp({ isPopUpOpen: false, salesOrder: null })
              }}
            >
              <DeleteSalesOrder
                closePopUp={() => {
                  setDeletePopUp({ isPopUpOpen: false, salesOrder: null })
                }}
                salesOrder={deletePopUp.salesOrder}
                plantId={plantId?.id}
              />
            </Popup>
          )}
        </WhiteCard>
      ) : (
        <FullPageLoader />
      )}
    </MotionDiv>
  )
}

export default ViewSalesOrder
